import { useCallback, useState } from 'react';
import { LactateTest, ReportType } from '../types';
import {
  createLactateTest,
  uploadChartImage,
  requestReportGeneration,
} from '../services/lactateTestService';

type CreateInput = Omit<
  LactateTest,
  'created_at' | 'updated_at' | 'pdf_url' | 'report_id' | 'prior_initial_test_id'
>;

export interface GenerateResult {
  url: string;
  download_url: string;
  report_type: ReportType;
  prior_initial_found: boolean;
}

export interface SubmitDeps {
  createLactateTest: (input: CreateInput) => Promise<{ testId: string }>;
  uploadChartImage: (clientId: string, testId: string, file: File) => Promise<string>;
  requestReportGeneration: (clientId: string, testId: string) => Promise<GenerateResult>;
}

export interface SubmitArgs {
  input: CreateInput;
  clientId: string;
  file: File | null;
}

export async function runSubmitLactateTest(
  args: SubmitArgs,
  deps: SubmitDeps
): Promise<GenerateResult> {
  const { testId } = await deps.createLactateTest(args.input);
  if (args.file) {
    await deps.uploadChartImage(args.clientId, testId, args.file);
  }
  return deps.requestReportGeneration(args.clientId, testId);
}

const defaultDeps: SubmitDeps = { createLactateTest, uploadChartImage, requestReportGeneration };

export interface SubmitState {
  isGenerating: boolean;
  progress: number;
  url?: string;
  downloadUrl?: string;
  reportType?: ReportType;
  priorInitialFound?: boolean;
  error: string | null;
}

export function useSubmitLactateTest(deps: SubmitDeps = defaultDeps) {
  const [state, setState] = useState<SubmitState>({
    isGenerating: false,
    progress: 0,
    error: null,
  });

  const submit = useCallback(
    async (args: SubmitArgs) => {
      setState({ isGenerating: true, progress: 10, error: null });
      try {
        setState((s) => ({ ...s, progress: 40 }));
        const result = await runSubmitLactateTest(args, deps);
        setState({
          isGenerating: false,
          progress: 100,
          url: result.url,
          downloadUrl: result.download_url,
          reportType: result.report_type,
          priorInitialFound: result.prior_initial_found,
          error: null,
        });
        return result;
      } catch (err) {
        setState({
          isGenerating: false,
          progress: 0,
          error: err instanceof Error ? err.message : 'Report generation failed',
        });
        throw err;
      }
    },
    [deps]
  );

  const reset = useCallback(
    () => setState({ isGenerating: false, progress: 0, error: null }),
    []
  );

  return { state, submit, reset };
}
